'use client';

import { ReactNode } from 'react';
import { Transition } from '@headlessui/react';
import { clsx } from 'clsx';

import {
  useScrollProgress,
  useShallowAppearance,
  UseScrollProgressArgs,
  UseShallowAppearanceArgs,
} from '@monoimin/hooks';
import { IconButton } from '@monoimin/atoms';
type RequiredHooksArgs = Pick<
  UseShallowAppearanceArgs,
  'closedStorageKey' | 'preventOpeningStorageKey'
>;

type OptionalHooksArgs = Partial<
  Pick<UseShallowAppearanceArgs, 'expiration'> &
    Pick<UseScrollProgressArgs, 'start' | 'freeze'>
>;

type RenderChildren = (args: {
  show: boolean;
  onPreventOpen: () => void;
}) => ReactNode;

type PopupProps = RequiredHooksArgs &
  OptionalHooksArgs & {
    classNames?: {
      container?: string;
      closeButtonIcon?: string;
      closeButton?: string;
    };
    dataTestId?: string;
    children?: RenderChildren | ReactNode;
    onClose?: () => void;
  };

const EXPIRATION = 14 * 86_400 * 1000;

export const Popup = ({
  closedStorageKey,
  preventOpeningStorageKey,
  expiration = EXPIRATION,
  start = '20%',
  freeze = true,
  children,
  classNames,
  dataTestId,
  onClose,
}: PopupProps) => {
  const isScrolled = useScrollProgress({ start, freeze });

  const {
    show,
    onClose: onCloseInternal,
    onPreventOpen,
  } = useShallowAppearance({
    closedStorageKey,
    preventOpeningStorageKey,
    expiration,
  });

  const handleClose = () => {
    onClose?.();
    onCloseInternal();
  };

  const showPopup = show && isScrolled;

  return (
    <Transition show={showPopup}>
      <div
        data-testid={dataTestId}
        className={clsx(
          'fixed bottom-0 w-full inset-x-0 z-20 select-none',
          classNames?.container
        )}
      >
        {typeof children === 'function'
          ? children({ show, onPreventOpen })
          : children}
        <IconButton
          data-testid='popup-close-button'
          aria-label='Close button'
          className={clsx('button absolute', classNames?.closeButton)}
          onClick={handleClose}
          iconProps={{
            icon: 'closeXL',
            isInteractive: true,
            className: classNames?.closeButtonIcon,
          }}
        />
      </div>
    </Transition>
  );
};

export default Popup;
